/**
 * R1 (recette du 22.10.2026) — simulation en mode aperçu TOLÉRANT. Les obstacles de livraison (mesure absente, écran ou bloc sur mesure en squelette,
 * champ dégradé, texte repris du français…) ne bloquent pas la simulation d'une version : ils deviennent des points à corriger avant livraison, montrés
 * dans un bandeau repliable. Seules les erreurs de structure refusent la simulation (`simulation.spec_errors`, liste des seules erreurs bloquantes).
 * Règle i18n : identifiants statiques, jamais de clé composée.
 */
import type { GeneratorIssue } from '../data/contracts';
import type { DashboardBlock } from '@/uptk/core/components/dashboard/data/types';
import { generatorIssueLabel, type Translate } from '../generator/generatorI18n';

export const SIMULATION_DELIVERY_ID = {
  summary: 'moduleStudio.simulation.delivery.summary',
  intro: 'moduleStudio.simulation.delivery.intro',
  open: 'moduleStudio.simulation.delivery.open',
  customPage: 'moduleStudio.simulation.issue.customPage',
  customWidget: 'moduleStudio.simulation.issue.customWidget',
  customPageTitle: 'moduleStudio.simulation.customPage.title',
  customPageHelp: 'moduleStudio.simulation.customPage.help',
  measureMissing: 'moduleStudio.simulation.dashboard.measureMissing',
  customBlock: 'moduleStudio.simulation.dashboard.customBlock',
  specErrorsTitle: 'moduleStudio.simulation.specErrors.title',
  specErrorsHelp: 'moduleStudio.simulation.specErrors.help',
} as const;

/** Points à corriger avant livraison : erreurs de livraison tolérées par l'aperçu et avertissements (les notes restent dans « À propos »). */
export const deliveryPoints = (issues: readonly GeneratorIssue[] | undefined): GeneratorIssue[] =>
  (issues ?? []).filter(issue => issue.severity === 'error' || issue.severity === 'warning');

/** Une correction par code et bloc/page : les variantes de langue ne multiplient pas les points. */
export function groupedDeliveryPoints(issues: readonly GeneratorIssue[] | undefined): GeneratorIssue[] {
  const groups = new Map<string, GeneratorIssue>();
  for (const issue of deliveryPoints(issues)) {
    const path = issue.path.replace(/^(\/dashboard\/widgets\/[^/]+|\/pages\/[^/]+)(?:\/.*)?$/, '$1');
    const key = JSON.stringify([issue.code, path]);
    const previous = groups.get(key);
    if (!previous || issue.severity === 'error') groups.set(key, {...issue, path});
  }
  return [...groups.values()].sort((a,b)=>Number(b.severity==='error')-Number(a.severity==='error') || a.code.localeCompare(b.code) || a.path.localeCompare(b.path));
}

/** Libellé d'un point en mots du Maker ; un code inconnu est rendu tel quel. */
export const simulationIssueLabel = (t: Translate, issue: Pick<GeneratorIssue, 'code' | 'detail'>): string => {
  const code: string = issue.code;
  switch (code) {
    case 'custom_page.skeleton': return t(SIMULATION_DELIVERY_ID.customPage, { detail: issue.detail });
    case 'custom_widget.skeleton': return t(SIMULATION_DELIVERY_ID.customWidget, { detail: issue.detail });
    default: return generatorIssueLabel(t, code, issue.detail);
  }
};

const MEASURED_BLOCKS: ReadonlySet<string> = new Set(['kpi', 'chart']);
/** Un bloc chiffré sans mesure (`widget.measure_missing`) : rendu « — » « Mesure à définir », jamais un calcul. */
export const blockMeasureMissing = (block: Pick<DashboardBlock, 'type' | 'measures'>): boolean =>
  MEASURED_BLOCKS.has(block.type) && !(block.measures?.length);

/** Titre d'un bloc dans la langue de la simulation, repli français puis clé du bloc (`i18n.fallback_fr`). */
export const blockTitle = (block: Pick<DashboardBlock, 'title' | 'key'>, locale: string): string =>
  block.title?.[locale] ?? block.title?.fr ?? block.key;
